/** Zapier / paste / webhook envelope helpers for IdeaRoom JSON. */

const ZAPIER_WRAP_KEYS = ["json", "payload", "data", "text", "message", "body"] as const;

export function asIdeaRoomRecord(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, unknown>;
  return null;
}

export function ideaRoomStr(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return "";
}

export function ideaRoomHttpUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!/^https?:\/\//i.test(s)) return null;
  return s;
}

function looksLikeIdeaRoom(rec: Record<string, unknown>): boolean {
  return Boolean(
    ideaRoomStr(rec.hash) || asIdeaRoomRecord(rec.order) || rec.eventType || rec.idearoom_order_id || rec.uuid
  );
}

export function ideaRoomHashFromUrl(url: string): string {
  const hashAt = url.indexOf("#");
  if (hashAt < 0) return "";
  const frag = url.slice(hashAt + 1).split(/[&/?]/)[0]?.trim() ?? "";
  return /^[a-f0-9]{32}$/i.test(frag) ? frag : "";
}

export function coerceIdeaRoomPayload(payload: unknown): unknown {
  if (typeof payload === "string") {
    const parsed = parseIdeaRoomExportJson(payload);
    return parsed.ok ? parsed.payload : payload;
  }
  const rec = asIdeaRoomRecord(payload);
  if (!rec) return payload;
  if (looksLikeIdeaRoom(rec)) return rec;
  for (const key of ZAPIER_WRAP_KEYS) {
    const v = rec[key];
    if (typeof v === "string") {
      const parsed = parseIdeaRoomExportJson(v);
      if (parsed.ok) return parsed.payload;
    }
    const nested = asIdeaRoomRecord(v);
    if (nested && looksLikeIdeaRoom(nested)) return nested;
  }
  return rec;
}

/** Floor-plan render PNG from a real IdeaRoom `order.images` object (not the configurator homepage). */
export function ideaRoomFloorPlanUrl(body: Record<string, unknown>): string | null {
  const order = asIdeaRoomRecord(body.order);
  const images = asIdeaRoomRecord(body.images) ?? (order ? asIdeaRoomRecord(order.images) : null);
  if (!images) return null;
  return (
    ideaRoomHttpUrl(images.floorplanWithLegendSrc) ??
    ideaRoomHttpUrl(images.floorplanSrc) ??
    ideaRoomHttpUrl(images.floorPlanSrc) ??
    ideaRoomHttpUrl(images.floorplanEmailSrc)
  );
}

/** IdeaRoom puts `notes` on `order`, not the webhook envelope. */
export function ideaRoomNotes(body: Record<string, unknown>): string | null {
  const order = asIdeaRoomRecord(body.order);
  const nested = order ? ideaRoomStr(order.notes) : "";
  return nested || null;
}

function extractFirstJsonObject(text: string): string | null {
  const start = text.indexOf("{");
  if (start < 0) return null;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < text.length; i++) {
    const c = text[i];
    if (inString) {
      if (escape) {
        escape = false;
        continue;
      }
      if (c === "\\") {
        escape = true;
        continue;
      }
      if (c === '"') inString = false;
      continue;
    }
    if (c === '"') {
      inString = true;
      continue;
    }
    if (c === "{") depth += 1;
    else if (c === "}") {
      depth -= 1;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

export function parseIdeaRoomExportJson(text: string): { ok: true; payload: unknown } | { ok: false; error: string } {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, error: "Paste the IdeaRoom JSON export." };
  try {
    return { ok: true, payload: JSON.parse(trimmed) as unknown };
  } catch {
    const slice = extractFirstJsonObject(trimmed);
    if (slice) {
      try {
        return { ok: true, payload: JSON.parse(slice) as unknown };
      } catch {
        /* fall through */
      }
    }
    return { ok: false, error: "IdeaRoom body must be JSON." };
  }
}

export function ideaRoomRawPayload(payload: unknown): Record<string, unknown> {
  const coerced = coerceIdeaRoomPayload(payload);
  const raw = asIdeaRoomRecord(coerced) ?? { value: coerced };
  const stamped = { ...raw };
  const floor = ideaRoomFloorPlanUrl(stamped);
  if (floor && !ideaRoomHttpUrl(stamped.floor_plan_url)) stamped.floor_plan_url = floor;
  const notes = ideaRoomNotes(stamped);
  if (notes && !ideaRoomStr(stamped.notes)) stamped.notes = notes;
  return stamped;
}
